import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, ArrowUpRight, X, SlidersHorizontal, List, Columns3, Calendar as CalIcon } from "lucide-react";
import { PHASE_KEYS, PROJECT_STATUS, fmtDateOnly } from "@/lib/workflow";
import { BoardView } from "@/components/projects/BoardView";
import { CalendarView } from "@/components/projects/CalendarView";
import { SavedViews } from "@/components/dashboard/SavedViews";

const EMPTY = { pm_id: "all", customer: "all", brand: "all", project_type: "all", phase: "all", status_filter: "all", erp_readiness: "all", date_from: "", date_to: "" };
const ERP = [{ value: "all", label: "All readiness" }, { value: "ready", label: "ERP ready" }, { value: "not_ready", label: "Not ERP ready" }];
const MODES = [{ value: "list", label: "List", icon: List }, { value: "calendar", label: "Calendar", icon: CalIcon }, { value: "board", label: "Board", icon: Columns3 }];
const GROUPBY = [{ value: "phase", label: "Phase" }, { value: "status", label: "Status" }, { value: "erp", label: "ERP readiness" }];

function Sel({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 bg-white border-slate-200 text-sm" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = ["admin", "project_coordinator", "project_manager"].includes(user?.role);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY);
  const [scope, setScope] = useState("all");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("projects_view_mode") || "list");
  const [groupBy, setGroupBy] = useState("phase");
  const [options, setOptions] = useState({ customers: [], brands: [], managers: [], projectTypes: [] });
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => { localStorage.setItem("projects_view_mode", viewMode); }, [viewMode]);

  useEffect(() => {
    Promise.all([api.get("/projects/filters"), api.get("/project-types"), api.get("/project-views")]).then(([f, t, v]) => {
      setOptions({ ...f.data, projectTypes: (t.data.project_types || []).map((x) => x.name) });
      const vs = v.data.views || [];
      setViews(vs);
      const def = vs.find((x) => x.is_default);
      if (def) applyViewObj(def);
      setReady(true);
    }).catch(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!ready) return;
    setLoading(true);
    api.get("/projects", { params: { scope, search, ...filters } }).then((r) => setProjects(r.data)).finally(() => setLoading(false));
  }, [ready, scope, filters, search]);

  const reloadViews = () => api.get("/project-views").then((r) => r.data.views || []);

  const applyViewObj = (v) => {
    setFilters({ ...EMPTY, ...v.filters });
    setScope(v.scope || "all");
    setViewMode(v.view_mode || "list");
    setGroupBy(v.group_by || "phase");
    setActiveViewId(v.view_id);
  };
  const applyView = (id) => { if (!id) { setActiveViewId(null); return; } const v = views.find((x) => x.view_id === id); if (v) applyViewObj(v); };

  const viewPayload = () => ({ scope, view_mode: viewMode, group_by: groupBy, sort: "created_desc", filters });
  const onSave = async ({ name, is_default }) => { const r = await api.post("/project-views", { name, ...viewPayload(), is_default }); setViews(await reloadViews()); setActiveViewId(r.data.view_id); };
  const onUpdate = async (id) => { await api.put(`/project-views/${id}`, viewPayload()); setViews(await reloadViews()); };
  const onRename = async (id, name) => { await api.put(`/project-views/${id}`, { name }); setViews(await reloadViews()); };
  const onDelete = async (id) => { await api.delete(`/project-views/${id}`); setViews(await reloadViews()); if (activeViewId === id) setActiveViewId(null); };
  const onSetDefault = async (id, value) => { await api.put(`/project-views/${id}`, { is_default: value }); setViews(await reloadViews()); };

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const activeCount = Object.entries(filters).filter(([k, v]) => (k.startsWith("date_") ? !!v : v !== "all")).length + (scope === "mine" ? 1 : 0);

  const sorted = useMemo(() => [...projects].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")), [projects]);

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline">Project operations</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="projects-page-title">Projects</h1>
          <p className="text-sm text-slate-500 mt-1">Your portfolio across every phase and ERP readiness.</p>
        </div>
        {canCreate && <Button onClick={() => navigate("/projects/new")} className="bg-[#2563EB] hover:bg-[#1d4ed8] h-10" data-testid="project-registry-create-btn">
          <Plus className="h-4 w-4 mr-2" />Create New Project
        </Button>}
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="projects-view-switcher">
          {MODES.map((m) => { const Icon = m.icon; return (
            <button key={m.value} onClick={() => setViewMode(m.value)} data-testid={`view-mode-${m.value}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition ${viewMode === m.value ? "bg-[#2563EB] text-white" : "text-slate-600 hover:text-slate-900"}`}>
              <Icon className="h-3.5 w-3.5" />{m.label}
            </button>
          ); })}
        </div>
        <SavedViews views={views} activeViewId={activeViewId} onSelect={applyView} onSave={onSave} onUpdate={onUpdate} onRename={onRename} onDelete={onDelete} onSetDefault={onSetDefault} />
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-3" data-testid="projects-filters">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <SlidersHorizontal className="h-4 w-4 text-slate-500" />
          <span className="overline">Filters</span>
          <div className="ml-2 inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="projects-scope-toggle">
            {[{ v: "mine", l: "My projects" }, { v: "all", l: "All accessible" }].map((o) => (
              <button key={o.v} onClick={() => setScope(o.v)} data-testid={`projects-scope-${o.v}`}
                className={`px-3 py-1 text-xs font-medium rounded-md transition ${scope === o.v ? "bg-[#2563EB] text-white" : "text-slate-600 hover:text-slate-900"}`}>{o.l}</button>
            ))}
          </div>
          {viewMode === "board" && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-slate-500">Group by</span>
              <div className="w-40"><Sel value={groupBy} onChange={setGroupBy} options={GROUPBY} testId="board-groupby" /></div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
          <div className="relative col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search code, customer, brand…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="project-search-input" className="pl-9 bg-white border-slate-200 h-9" />
          </div>
          <Sel value={filters.pm_id} onChange={set("pm_id")} testId="filter-pm-select" options={[{ value: "all", label: "All managers" }, ...options.managers.map((m) => ({ value: m.user_id, label: m.name }))]} />
          <Sel value={filters.customer} onChange={set("customer")} testId="filter-customer-select" options={[{ value: "all", label: "All customers" }, ...options.customers.map((c) => ({ value: c, label: c }))]} />
          <Sel value={filters.brand} onChange={set("brand")} testId="filter-brand-select" options={[{ value: "all", label: "All brands" }, ...options.brands.map((b) => ({ value: b, label: b }))]} />
          <Sel value={filters.project_type} onChange={set("project_type")} testId="filter-type-select" options={[{ value: "all", label: "All types" }, ...options.projectTypes.map((t) => ({ value: t, label: t }))]} />
          <Sel value={filters.phase} onChange={set("phase")} testId="filter-phase-select" options={PHASE_KEYS} />
          <Sel value={filters.status_filter} onChange={set("status_filter")} testId="filter-status-select" options={PROJECT_STATUS} />
          <Sel value={filters.erp_readiness} onChange={set("erp_readiness")} testId="filter-erp-select" options={ERP} />
          <div className="flex items-center gap-1 col-span-2 lg:col-span-1">
            <Input type="date" value={filters.date_from} onChange={(e) => set("date_from")(e.target.value)} className="dash-date h-9 flex-1 min-w-0 bg-white border-slate-200 text-xs" data-testid="filter-date-from" />
            <span className="text-slate-400 text-xs shrink-0">→</span>
            <Input type="date" value={filters.date_to} onChange={(e) => set("date_to")(e.target.value)} className="dash-date h-9 flex-1 min-w-0 bg-white border-slate-200 text-xs" data-testid="filter-date-to" />
          </div>
          {activeCount > 0 && (
            <Button variant="ghost" className="h-9 justify-start text-slate-500 col-span-2 lg:col-span-1" onClick={() => { setFilters(EMPTY); setScope("all"); }} data-testid="filters-clear-btn"><X className="h-4 w-4 mr-1" />Clear {activeCount}</Button>
          )}
        </div>
      </div>

      <p className="mt-4 text-sm text-slate-500" data-testid="projects-count">{loading ? "Loading…" : `${sorted.length} project${sorted.length === 1 ? "" : "s"}`}</p>

      {viewMode === "board" ? <BoardView projects={sorted} groupBy={groupBy} />
        : viewMode === "calendar" ? <CalendarView projects={sorted} />
        : (
          <div className="mt-2 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="project-registry-table">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="w-32">Project #</TableHead>
                  <TableHead>Customer / Brand</TableHead>
                  <TableHead>PM</TableHead>
                  <TableHead>Current phase</TableHead>
                  <TableHead className="w-40">ERP readiness</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
                ) : sorted.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500" data-testid="projects-empty">No projects match these filters.</TableCell></TableRow>
                ) : sorted.map((p) => (
                  <TableRow key={p.project_id} className="cursor-pointer row-hover" onClick={() => navigate(`/projects/${p.project_id}`)} data-testid={`project-table-row-${p.project_number}`}>
                    <TableCell className="font-mono text-xs">{p.project_number}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-slate-900">{p.customer_name}</div>
                      <div className="text-xs text-slate-500">{p.brand} · {p.project_type}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{p.project_manager_name}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{p.current_phase_name || p.lifecycle_phase}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={p.readiness_pct || 0} className="h-1.5 w-20" />
                        {p.erp_ready ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Ready</Badge> : <span className="text-xs text-slate-400">{p.readiness_pct || 0}%</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtDateOnly(p.target_date)}</TableCell>
                    <TableCell><ArrowUpRight className="h-4 w-4 text-slate-300" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
    </div>
  );
}
